import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight, ChevronLeft, Globe, Users, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { ParallaxBackground } from './utils/parallax';
import { bootMark } from '../debug/bootTracer';
import { SafeStorage } from '../utils/safeStorage';

interface OnboardingScreenProps {
  onComplete: () => void;
}

const slides = [
  {
    icon: Globe,
    title: 'Make New Connections',
    description: 'Connect with likeminded African, Caribbean and America diaspora from around the world.',
    gradient: 'from-[#4478ea] to-[#2d1ef1]',
    darkGradient: 'dark:from-[#4478ea] dark:to-[#2d1ef1]',
  },
  {
    icon: Users,
    title: 'Explore Our Identity',
    description: 'Explore African history and join communities that celebrate our diverse cultures.',
    gradient: 'from-[#2d1ef1] to-[#4478ea]',
    darkGradient: 'dark:from-[#2d1ef1] dark:to-[#4478ea]',
  },
  {
    icon: Sparkles,
    title: 'Join The Conversation',
    description: 'Post yaps and shouts, chat with friends, and engage with content that educates and empowers. Your voice matters.',
    gradient: 'from-[#4478ea] via-[#2d1ef1] to-[#2C3E50]',
    darkGradient: 'dark:from-[#4478ea] dark:via-[#2d1ef1] dark:to-[#2C3E50]',
  },
];

export function OnboardingScreen({ onComplete }: OnboardingScreenProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (showWelcome) {
      bootMark('WelcomeModalRender');
    }
  }, []);

  const handleDismissWelcome = () => {
    bootMark('WelcomeModalDismiss');
    try {
      SafeStorage.setItem('afroyapp_welcome_dismissed', 'true');
    } catch (e) {
      console.error('SafeStorage fail', e);
    }
    setShowWelcome(false);
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      // Slide 4 uses its own buttons — this should never fire from slide 4
      onComplete();
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const skipOnboarding = () => {
    onComplete();
  };

  // Slide navigation
  const handleSetUpNow = () => {};
  const handleSkipSecurity = () => {};

  const IconComponent = slides[currentSlide].icon;

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex flex-col items-center justify-between p-8 relative">
      {showWelcome && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50">
          <div className="bg-[#e7d299] dark:bg-[#1a1a1a] p-6 rounded-xl shadow-lg max-w-sm w-full text-center">
            <h2 className="text-2xl font-bold mb-4">Welcome to AfroYap!</h2>
            <p className="mb-6 text-[#2C3E50] dark:text-[#E5E5E5]">Explore, connect, and share your story.</p>
            <Button onClick={handleDismissWelcome}>Got it</Button>
          </div>
        </div>
      )}

      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>

      <div className="w-full flex justify-between relative z-10">
        <button
          onClick={skipOnboarding}
          className="text-[#373737] dark:text-white hover:text-[#4478ea] transition-colors text-xs font-medium"
        >
          Skip
        </button>
        {currentSlide > 0 && (
          <button onClick={prevSlide} className="flex items-center text-[#373737] dark:text-white hover:text-[#4478ea] transition-colors text-xs font-medium">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          className="relative z-10 flex-1 flex flex-col items-center justify-center text-center max-w-md"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className={`w-16 h-16 rounded-full bg-linear-to-br ${slides[currentSlide].gradient} ${slides[currentSlide].darkGradient} flex items-center justify-center mb-6 shadow-lg`}
          >
            {IconComponent && (
              <IconComponent className="w-8 h-8 text-white" />
            )}
          </motion.div>

          <h2
            className="text-3xl mb-3 header-text-shadow"
            style={{
              fontFamily: "'NarnialOne', 'Narniaone', 'NARNIAONE', 'Poppins', sans-serif"
            }}
          >
            {slides[currentSlide].title}
          </h2>
          <p className="text-sm text-[#373737] dark:text-[#E5E5E5] leading-relaxed max-w-[280px]" style={{ fontFamily: "'REM', 'Rem', 'rem', 'Poppins', sans-serif" }}>
            {slides[currentSlide].description}
          </p>
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 w-full flex flex-col items-center gap-8 mb-4">
        <div className="flex justify-center gap-2">
          {slides.map((_, index) => (
            <motion.div
              key={index}
              animate={{
                width: index === currentSlide ? 24 : 8,
              }}
              className={`h-1.5 rounded-full transition-colors ${index === currentSlide
                ? 'bg-[#F39C12] dark:bg-[#4478ea]'
                : 'bg-black/10 dark:bg-white/10'
                }`}
            />
          ))}
        </div>

          <Button
            onClick={nextSlide}
            className="w-[320px] h-12 px-6 text-white border-none hover:opacity-90 transition-all rounded-xl font-bold text-base shadow-lg active:scale-[0.98] bg-linear-to-br from-[#F39C12] to-[#E74C3C] dark:bg-linear-to-br dark:from-[#4478ea] dark:to-[#2d1ef1]"
          >
            {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
      </div>

    </div>
  );
}
